import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Trash2, Plus, GripVertical, Image as ImageIcon, Type, FolderOpen, MoveUp, MoveDown } from 'lucide-react';

const TickerTapeEditor = ({ content, onChange, openMediaPicker, blockId }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  const items = content.items || [];
  
  // Add a new item
  const addItem = (type = 'text') => {
    const newItem = {
      id: `item-${Date.now()}`,
      type,
      text: type === 'text' ? 'Your text here' : '',
      imageUrl: '',
      imageAlt: '',
      link: '',
    };
    onChange({ ...content, items: [...items, newItem] });
  };
  
  // Update an item
  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onChange({ ...content, items: newItems });
  };
  
  // Remove an item
  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange({ ...content, items: newItems });
  };
  
  // Move item up/down
  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    onChange({ ...content, items: newItems });
  };
  
  // Open media picker for a specific item
  const handleOpenMediaPicker = (itemIndex) => {
    openMediaPicker(`${blockId}:ticker:${itemIndex}`);
  };

  return (
    <div className="space-y-4">
      {/* Ticker Settings */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Ticker Settings</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Animation Style */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Animation</Label>
            <Select
              value={content.animationStyle || 'continuous'}
              onValueChange={(value) => onChange({ ...content, animationStyle: value })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="continuous">Continuous Scroll</SelectItem>
                <SelectItem value="step">Step-by-Step</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Direction */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Direction</Label>
            <Select
              value={content.direction || 'left'}
              onValueChange={(value) => onChange({ ...content, direction: value })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">← Left</SelectItem>
                <SelectItem value="right">→ Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Speed */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Speed</Label>
            <Select
              value={content.speed || 'medium'}
              onValueChange={(value) => onChange({ ...content, speed: value })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="slow">Slow</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="fast">Fast</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Pause on Hover */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Hover Behavior</Label>
            <Select
              value={content.pauseOnHover ? 'pause' : 'continue'}
              onValueChange={(value) => onChange({ ...content, pauseOnHover: value === 'pause' })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pause">Pause on Hover</SelectItem>
                <SelectItem value="continue">Continue on Hover</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Styling Settings */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Styling</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Background Color */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Background</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={content.backgroundColor || '#f8fafc'}
                onChange={(e) => onChange({ ...content, backgroundColor: e.target.value })}
                className="w-8 h-8 rounded border cursor-pointer"
              />
              <label className="flex items-center gap-1 text-xs text-slate-500">
                <input
                  type="checkbox"
                  checked={content.transparentBg || false}
                  onChange={(e) => onChange({ ...content, transparentBg: e.target.checked })}
                  className="w-3 h-3 rounded"
                />
                None
              </label>
            </div>
          </div>
          
          {/* Item Spacing */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Item Spacing</Label>
            <Input
              type="number"
              value={content.itemSpacing || 48}
              onChange={(e) => onChange({ ...content, itemSpacing: parseInt(e.target.value) || 48 })}
              className="h-8 text-xs"
              min="16"
              max="200"
            />
          </div>
          
          {/* Item Size (for logos) */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Logo/Image Height</Label>
            <Input
              type="number"
              value={content.itemHeight || 48}
              onChange={(e) => onChange({ ...content, itemHeight: parseInt(e.target.value) || 48 })}
              className="h-8 text-xs"
              min="24"
              max="200"
            />
          </div>
          
          {/* Vertical Padding */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Vertical Padding</Label>
            <Input
              type="number"
              value={content.verticalPadding || 16}
              onChange={(e) => onChange({ ...content, verticalPadding: parseInt(e.target.value) || 16 })}
              className="h-8 text-xs"
              min="0"
              max="100"
            />
          </div>
          
          {/* Grayscale Option */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Logo Filter</Label>
            <Select
              value={content.logoFilter || 'none'}
              onValueChange={(value) => onChange({ ...content, logoFilter: value })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Full Color</SelectItem>
                <SelectItem value="grayscale">Grayscale</SelectItem>
                <SelectItem value="grayscale-hover">Grayscale → Color on Hover</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Items List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Ticker Items ({items.length})</Label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => addItem('image')}
              className="h-7 text-xs gap-1"
            >
              <ImageIcon size={12} />
              Add Logo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => addItem('text')}
              className="h-7 text-xs gap-1"
            >
              <Type size={12} />
              Add Text
            </Button>
          </div>
        </div>
        
        {items.length === 0 && (
          <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
            <p className="text-slate-400 text-sm">No items yet. Add logos or text to create your ticker tape.</p>
          </div>
        )}
        
        <div className="space-y-2">
          {items.map((item, index) => (
            <div 
              key={item.id || index}
              className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg group"
            >
              {/* Drag Handle Placeholder */}
              <div className="text-slate-300 cursor-grab">
                <GripVertical size={16} />
              </div>
              
              {/* Item Type Icon */}
              <div className={`w-8 h-8 rounded flex items-center justify-center ${item.type === 'image' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'}`}>
                {item.type === 'image' ? <ImageIcon size={14} /> : <Type size={14} />}
              </div>
              
              {/* Item Content */}
              <div className="flex-1 min-w-0">
                {item.type === 'image' ? (
                  <div className="flex items-center gap-2">
                    {item.imageUrl ? (
                      <>
                        <img 
                          src={item.imageUrl} 
                          alt={item.imageAlt || ''} 
                          className="h-8 max-w-[100px] object-contain rounded"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenMediaPicker(index)}
                          className="h-7 text-xs"
                        >
                          Change
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateItem(index, { imageUrl: '', imageAlt: '' })}
                          className="h-7 text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMediaPicker(index)}
                        className="h-7 text-xs gap-1"
                      >
                        <FolderOpen size={12} />
                        Select Image
                      </Button>
                    )}
                    <Input
                      value={item.link || ''}
                      onChange={(e) => updateItem(index, { link: e.target.value })}
                      placeholder="Link URL (optional)"
                      className="h-7 text-xs flex-1 max-w-[200px]"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      value={item.text || ''}
                      onChange={(e) => updateItem(index, { text: e.target.value })}
                      placeholder="Enter text..."
                      className="h-8 text-sm flex-1"
                    />
                    <Input
                      value={item.link || ''}
                      onChange={(e) => updateItem(index, { link: e.target.value })}
                      placeholder="Link URL (optional)"
                      className="h-7 text-xs w-[180px]"
                    />
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                >
                  <MoveUp size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                >
                  <MoveDown size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Preview */}
      {items.length > 0 && (
        <div className="mt-4">
          <Label className="text-xs text-slate-500 mb-2 block">Preview (scroll direction: {content.direction || 'left'})</Label>
          <div 
            className="overflow-hidden rounded-lg border border-slate-200"
            style={{
              backgroundColor: content.transparentBg ? 'transparent' : (content.backgroundColor || '#f8fafc'),
              padding: `${content.verticalPadding || 16}px 0`,
            }}
          >
            <div 
              className="flex items-center"
              style={{ gap: `${content.itemSpacing || 48}px` }}
            >
              {items.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`flex-shrink-0 ${content.logoFilter === 'grayscale' ? 'grayscale' : ''}`}
                >
                  {item.type === 'image' ? (
                    item.imageUrl ? (
                      <img 
                        src={item.imageUrl} 
                        alt={item.imageAlt || ''} 
                        style={{ height: `${content.itemHeight || 48}px`, maxWidth: '150px', objectFit: 'contain' }}
                      />
                    ) : (
                      <div 
                        className="bg-slate-200 rounded flex items-center justify-center text-slate-400"
                        style={{ width: '100px', height: `${content.itemHeight || 48}px` }}
                      >
                        <ImageIcon size={20} />
                      </div>
                    )
                  ) : (
                    <span className="text-slate-700 font-medium whitespace-nowrap">
                      {item.text || 'Text item'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TickerTapeEditor;
